// ==============================================================================================
//  ProgressStepper – Numbered step indicator for multi-page flows
//  Used during account creation: Register → Company Setup → Trial Success
// ==============================================================================================

import React from 'react';

/**
 * @param {Object} props
 * @param {Array<{label: string}>} props.steps - Step definitions
 * @param {number} props.currentStep - 0-indexed current step
 * @param {'dark'|'light'} [props.variant='dark'] - Color scheme
 */
export default function ProgressStepper({ steps, currentStep, variant = 'dark' }) {
  const isDark = variant === 'dark';

  return (
    <div className="w-full max-w-lg mx-auto mb-8">
      <div className="flex items-center justify-between">
        {steps.map((step, idx) => {
          const isCompleted = idx < currentStep;
          const isActive = idx === currentStep;
          const isLast = idx === steps.length - 1;

          return (
            <React.Fragment key={idx}>
              {/* Step circle + label */}
              <div className="flex flex-col items-center relative">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all duration-300 ${
                    isCompleted
                      ? 'bg-emerald-500 border-emerald-500 text-white'
                      : isActive
                        ? 'bg-red-600 border-red-600 text-white shadow-lg shadow-red-600/30'
                        : isDark
                          ? 'bg-transparent border-gray-600 text-gray-500'
                          : 'bg-transparent border-gray-300 text-gray-400'
                  }`}
                >
                  {isCompleted ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    idx + 1
                  )}
                </div>
                <span
                  className={`mt-2 text-xs font-medium whitespace-nowrap ${
                    isCompleted
                      ? 'text-emerald-600'
                      : isActive
                        ? isDark ? 'text-white' : 'text-gray-900'
                        : isDark ? 'text-gray-500' : 'text-gray-400'
                  }`}
                >
                  {step.label}
                </span>
              </div>

              {/* Connector line */}
              {!isLast && (
                <div className="flex-1 mx-2 mt-[-1.25rem]">
                  <div className={`h-0.5 w-full rounded overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-300'}`}>
                    <div
                      className="h-full bg-emerald-500 transition-all duration-500"
                      style={{ width: isCompleted ? '100%' : '0%' }}
                    />
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------------------------
//  Build My Checks (c) 2025 Gareth Morgan - BuildMyChecks.co.uk
// ----------------------------------------------------------------------------------------------
